import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const apiUrl = process.env.REACT_APP_API_URL;

// Thunk for paginated users with caching
export const fetchUsersPaginated = createAsyncThunk(
  'usersManagement/fetchUsersPaginated',
  async (params, { getState }) => {
    const {
      userId,
      page = 1,
      pageSize = 50,
      sortField = 'FirstName',
      sortDirection = 'ASC',
      search = '',
      roleFilter = '',
      setupFilter = '',
      forceRefresh = false
    } = params;

    const state = getState();
    const cacheKey = `${page}_${pageSize}_${sortField}_${sortDirection}_${search}_${roleFilter}_${setupFilter}`;

    // Check if we have cached data and it's not stale (unless force refresh)
    if (!forceRefresh && state.usersManagement.usersCache[cacheKey] && state.usersManagement.usersCache[cacheKey].timestamp > Date.now() - 300000) { // 5 min cache
      return {
        ...state.usersManagement.usersCache[cacheKey].data,
        fromCache: true
      };
    }

    const response = await fetch(`${apiUrl}/dataServer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getUsersPaginated',
        payload: {
          userId,
          page,
          pageSize,
          sortField,
          sortDirection,
          search,
          roleFilter,
          setupFilter
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return {
      ...data,
      cacheKey,
      fromCache: false
    };
  }
);

const usersManagementSlice = createSlice({
  name: 'usersManagement',
  initialState: {
    // Paginated users state
    paginatedUsers: {
      data: [],
      salesReps: [],
      pagination: null,
      sort: null,
      filters: null,
      loading: false,
      error: null,
      lastFetch: null
    },

    // Cache for different page/filter combinations
    usersCache: {},

    status: 'idle',
    error: null,
  },
  reducers: {
    clearUsersCache: (state) => {
      state.usersCache = {};
    },
    invalidateUsersCache: (state, action) => {
      // Invalidate specific cache key or all cache
      if (action.payload) {
        delete state.usersCache[action.payload];
      } else {
        state.usersCache = {};
      }
    },
    updateUserInCache: (state, action) => {
      // Update a user across all cached results
      const { userId, updatedUser } = action.payload;

      // Update current data
      state.paginatedUsers.data = state.paginatedUsers.data.map(user =>
        user.UserId === userId ? { ...user, ...updatedUser } : user
      );

      // Update all cached results
      Object.keys(state.usersCache).forEach(cacheKey => {
        if (state.usersCache[cacheKey].data.data) {
          state.usersCache[cacheKey].data.data = state.usersCache[cacheKey].data.data.map(user =>
            user.UserId === userId ? { ...user, ...updatedUser } : user
          );
        }
      });
    },
    removeUserFromCache: (state, action) => {
      // Remove a user from all cached results
      const userId = action.payload;

      // Remove from current data
      state.paginatedUsers.data = state.paginatedUsers.data.filter(user => user.UserId !== userId);

      // Remove from all cached results
      Object.keys(state.usersCache).forEach(cacheKey => {
        if (state.usersCache[cacheKey].data.data) {
          state.usersCache[cacheKey].data.data = state.usersCache[cacheKey].data.data.filter(user => user.UserId !== userId);
        }
      });
    },
    updateSalesRepsInCache: (state, action) => {
      // Update sales reps data in current state and cache
      const salesReps = action.payload;

      state.paginatedUsers.salesReps = salesReps;

      // Update all cached results
      Object.keys(state.usersCache).forEach(cacheKey => {
        if (state.usersCache[cacheKey].data.salesReps) {
          state.usersCache[cacheKey].data.salesReps = salesReps;
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Paginated users reducers
      .addCase(fetchUsersPaginated.pending, (state) => {
        state.paginatedUsers.loading = true;
        state.paginatedUsers.error = null;
      })
      .addCase(fetchUsersPaginated.fulfilled, (state, action) => {
        const { data, salesReps, pagination, sort, filters, cacheKey, fromCache } = action.payload;

        state.paginatedUsers.loading = false;
        state.paginatedUsers.data = data;
        state.paginatedUsers.salesReps = salesReps;
        state.paginatedUsers.pagination = pagination;
        state.paginatedUsers.sort = sort;
        state.paginatedUsers.filters = filters;
        state.paginatedUsers.lastFetch = Date.now();

        // Cache the result if it's not from cache
        if (!fromCache && cacheKey) {
          state.usersCache[cacheKey] = {
            data: { data, salesReps, pagination, sort, filters },
            timestamp: Date.now()
          };
        }
      })
      .addCase(fetchUsersPaginated.rejected, (state, action) => {
        state.paginatedUsers.loading = false;
        state.paginatedUsers.error = action.error.message;
      });
  },
});

export const {
  clearUsersCache,
  invalidateUsersCache,
  updateUserInCache,
  removeUserFromCache,
  updateSalesRepsInCache
} = usersManagementSlice.actions;

export default usersManagementSlice.reducer;